
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useProfileStore } from "@/stores/profileStore";
import { useSystemStore } from "@/stores/systemStore";
import { AppSidebar } from "@/components/marketplace/AppSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Spinner } from "@/components/ui/Spinner";
import { logger } from "@/services/LoggingService";
import { WelcomeIntroGate } from "@/components/onboarding/WelcomeIntroGate";
import { PageContainer } from "@/components/ui/PageContainer";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const { 
    user, 
    session, 
    isInitializing, 
    isValidatingSession, 
    sessionValidated,
    sessionError,
    checkSessionValidity 
  } = useAuthStore();
  const { isCheckingProfile } = useProfileStore();
  const { storageBlocked, isOffline } = useSystemStore();

  // Check if we're on an OAuth callback route
  const isOAuthCallback = location.pathname === '/investments/connect' && 
    (location.search.includes('code=') || location.search.includes('error='));

  // Periodic session validation to catch server-side deletions
  useEffect(() => {
    if (!user || !session) return;

    // Check session validity every 30 seconds
    const interval = setInterval(async () => {
      await checkSessionValidity();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, session, checkSessionValidity]);

  // Debounced loader to avoid flash in previewer/HMR
  const shouldShowLoaderRaw = (isInitializing || isValidatingSession) || isCheckingProfile;
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    if (shouldShowLoaderRaw) {
      t = setTimeout(() => setShowLoader(true), 250);
    } else {
      setShowLoader(false);
    }
    return () => {
      if (t) clearTimeout(t);
    };
  }, [shouldShowLoaderRaw, isInitializing, isCheckingProfile, sessionValidated]);

  // Immediate redirect on session errors or storage issues
  if (sessionError || storageBlocked) {
    const errorParam = sessionError ? `error=${encodeURIComponent(sessionError)}` : 'error=storage_blocked';
    return <Navigate to={`/auth?${errorParam}`} replace />;
  }

  if (showLoader) {
    logger?.debug?.('AppLayout', 'Showing loader', {
      isInitializing,
      isValidatingSession,
      isCheckingProfile,
      sessionValidated
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
        {isCheckingProfile ? (
          <p className="ml-3 text-muted-foreground">Setting up your profile...</p>
        ) : (
          <p className="ml-3 text-muted-foreground">Loading...</p>
        )}
      </div>
    );
  }

  // Check both user AND session to handle logout timing
  // Allow OAuth callback routes to proceed even without full session
  if (!user || !session) {
    if (isOAuthCallback) {
      logger.info('AppLayout', 'Allowing OAuth callback to proceed without full session', {
        pathname: location.pathname,
        hasCode: location.search.includes('code='),
        hasError: location.search.includes('error=')
      });
      // Let the callback route handle authentication requirements
    } else {
      // Only redirect to auth if initialization/validation is complete and sessionValidated is true
      if (!isInitializing && !isValidatingSession && sessionValidated) {
        if (location.pathname.startsWith('/auth')) {
          return <>{children}</>;
        }
        const nextRaw = location.pathname + location.search;
        if (nextRaw === '/' || nextRaw === '') {
          return <Navigate to="/auth" replace />;
        }
        const next = encodeURIComponent(nextRaw);
        return <Navigate to={`/auth?redirect=${next}`} replace />;
      }
      // During initialization, session validation, or pending validation, show loader instead of redirecting
      if (isInitializing || isValidatingSession || !sessionValidated) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <Spinner size="lg" />
            <p className="ml-3 text-muted-foreground">Authenticating...</p>
          </div>
        );
      }
    }
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset>
          {isOffline && (
            <PageContainer withVerticalPadding={false} className="py-2">
              <div className="rounded-md border border-destructive/20 bg-destructive/10 text-destructive text-sm px-4 py-2">
                Network connection lost. Attempting to reconnect...
              </div>
            </PageContainer>
          )}
          {children}
          <WelcomeIntroGate />
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
